import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

export interface EmailLogEntry {
  id: string
  organizacion_id: string | null
  resend_id: string | null
  tipo: string
  from: string
  to: string[]
  subject: string
  status: string
  metadata: Record<string, unknown> | null
  error: string | null
  created_at: string
  updated_at: string
  body_html: string | null
  // ORR-170: `path` apunta al binario en el bucket `email-attachments`. null/ausente
  // en mails viejos (antes de ORR-170) o si la subida falló → sin descarga.
  attachments_meta: Array<{ filename: string; size: number; path?: string | null }> | null
  // Email of the user who triggered the send. NULL for system/cron-initiated emails.
  // NOTE: This field was added in migration 20260428100000. Regenerate database.ts with:
  // npx supabase gen types typescript --linked > src/types/database.ts
  enviado_por_email?: string | null
  // Cliente destinatario asociado al envío (ORR-109). NULL para mails de sistema/sin cliente.
  cliente_id?: string | null
}

export interface EmailSuppressionEntry {
  id: string
  organizacion_id: string | null
  email: string
  reason: string
  resend_id: string | null
  metadata: Record<string, unknown> | null
  created_at: string
}

/** Un valor registrado en `email_log` y cuántos mails lo tienen. */
export interface EmailLogFaceta {
  valor: string
  filas: number
}

/** Los valores de `tipo` y `status` presentes en el historial de una organización. */
export interface EmailLogFacetas {
  tipos: EmailLogFaceta[]
  estados: EmailLogFaceta[]
}

export interface EmailLogFilters {
  organizacionId?: string
  tipo?: string
  status?: string
  desde?: string
  hasta?: string
  // Filter by the actor who sent the email (maps to enviado_por_email column)
  enviadoPor?: string
  // Filter by the cliente the email was sent to (maps to cliente_id column, ORR-115)
  clienteId?: string
  limit?: number
  offset?: number
}

// El adjunto se abre en el momento: la URL firmada no necesita durar más.
const EMAIL_ADJUNTO_URL_TTL_SECONDS = 120

export const emailLogService = {
  async listar(
    client: SupabaseClient<Database>,
    filtros: EmailLogFilters = {}
  ): Promise<{ data: EmailLogEntry[]; count: number }> {
    if (!filtros.organizacionId) {
      throw new Error('organizacionId es requerido para listar email logs')
    }

    let query = client
      .from('email_log')
      // Exclude body_html from listing for performance (fetched only in detail view via obtenerPorId)
      .select('id, organizacion_id, resend_id, tipo, from, to, subject, status, metadata, error, attachments_meta, cliente_id, created_at, updated_at', { count: 'exact' })
      .or(`organizacion_id.eq.${filtros.organizacionId},organizacion_id.is.null`)
      .order('created_at', { ascending: false })
    if (filtros.tipo) {
      query = query.eq('tipo', filtros.tipo)
    }
    if (filtros.status) {
      query = query.eq('status', filtros.status)
    }
    if (filtros.desde) {
      query = query.gte('created_at', filtros.desde)
    }
    if (filtros.hasta) {
      query = query.lte('created_at', filtros.hasta)
    }
    const limit = filtros.limit ?? 20
    const offset = filtros.offset ?? 0
    query = query.range(offset, offset + limit - 1)

    if (filtros.enviadoPor) {
      query = query.eq('enviado_por_email', filtros.enviadoPor)
    }
    if (filtros.clienteId) {
      query = query.eq('cliente_id', filtros.clienteId)
    }

    const { data, error, count } = await query

    if (error) throw error
    return { data: (data ?? []) as EmailLogEntry[], count: count ?? 0 }
  },

  /**
   * Valores de `tipo` y `status` que la organización tiene registrados, con cuántos mails cada
   * uno. Alimenta las opciones de los filtros del historial: así el select ofrece lo que el
   * sistema manda y no una lista escrita a mano.
   *
   * El agrupado corre en Postgres. Leer la tabla desde acá para quedarse con los valores
   * distintos devuelve una lista incompleta: PostgREST trunca en 1000 filas y no avisa.
   *
   * Con `enviadoPor` cuenta solo los envíos de ese remitente. Sin él, cuenta toda la organización.
   *
   * El cast del cliente es porque `listar_facetas_email_log` no figura en los tipos generados.
   * REVISIT-IF: se regenera `database.ts` (ORR-305) → sacar el cast.
   */
  async facetas(
    client: SupabaseClient<Database>,
    organizacionId: string,
    enviadoPor?: string,
  ): Promise<EmailLogFacetas> {
    if (!organizacionId) {
      throw new Error('organizacionId es requerido para listar las facetas de email logs')
    }

    const { data, error } = await (client as unknown as SupabaseClient).rpc('listar_facetas_email_log', {
      p_organizacion_id: organizacionId,
      ...(enviadoPor ? { p_enviado_por: enviadoPor } : {}),
    })

    if (error) throw error

    const filas = (data ?? []) as unknown as Array<{
      faceta: string
      valor: string | null
      filas: number | string | null
    }>
    const tipos: EmailLogFaceta[] = []
    const estados: EmailLogFaceta[] = []

    for (const fila of filas) {
      if (!fila.valor) continue
      const destino = fila.faceta === 'tipo' ? tipos : fila.faceta === 'status' ? estados : null
      destino?.push({ valor: fila.valor, filas: Number(fila.filas ?? 0) || 0 })
    }

    return { tipos, estados }
  },

  async obtenerPorId(
    client: SupabaseClient<Database>,
    id: string,
    organizacionId: string
  ): Promise<EmailLogEntry> {
    const { data, error } = await client
      .from('email_log')
      .select('*')
      .eq('id', id)
      .or(`organizacion_id.eq.${organizacionId},organizacion_id.is.null`)
      .single()

    if (error) throw error
    return data as EmailLogEntry
  },

  // Signed URL para descargar un adjunto persistido en Storage.
  async crearUrlAdjunto(
    client: SupabaseClient<Database>,
    path: string,
    expiresIn: number = EMAIL_ADJUNTO_URL_TTL_SECONDS,
  ): Promise<string> {
    const { data, error } = await client.storage
      .from('email-attachments')
      .createSignedUrl(path, expiresIn)

    if (error) throw error
    return data.signedUrl
  },

  async listarSuprimidos(
    client: SupabaseClient<Database>,
    organizacionId?: string
  ): Promise<EmailSuppressionEntry[]> {
    let query = client
      .from('email_suppression_list')
      .select('*')
      .order('created_at', { ascending: false })

    if (organizacionId) {
      query = query.or(`organizacion_id.eq.${organizacionId},organizacion_id.is.null`)
    }

    const { data, error } = await query

    if (error) throw error
    return (data ?? []) as EmailSuppressionEntry[]
  },

  async eliminarSupresion(
    client: SupabaseClient<Database>,
    id: string
  ): Promise<void> {
    const { error } = await client
      .from('email_suppression_list')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  async agregarSupresionManual(
    client: SupabaseClient<Database>,
    email: string,
    organizacionId?: string
  ): Promise<void> {
    const { error } = await client
      .from('email_suppression_list')
      .insert({
        email,
        reason: 'manual',
        organizacion_id: organizacionId ?? null,
      })

    if (error) throw error
  },

  /**
   * Último envío por cliente. `tipo` acepta uno o varios: ORR-225 necesita la última
   * notificación "sin importar el medio" (estado de cuenta + recordatorio de deuda).
   */
  async ultimoEnvioPorClienteIds(
    client: SupabaseClient<Database>,
    clienteIds: string[],
    tipo: string | string[] = 'estado_cuenta',
    organizacionId?: string
  ): Promise<Record<string, string>> {
    if (clienteIds.length === 0) return {}

    // El DISTINCT ON corre en Postgres (ORR-237). Resolverlo acá exigía traer el listado
    // completo y quedarse con la primera fila de cada cliente, y cualquier recorte —el
    // `.limit(500)` que había, o el techo de 1000 que PostgREST aplica solo— deja afuera a
    // los clientes cuyo último envío es más viejo que el corte, que pasan a figurar "Nunca".
    const { data, error } = await client.rpc('ultimo_envio_por_cliente', {
      p_cliente_ids: clienteIds,
      p_tipos: Array.isArray(tipo) ? tipo : [tipo],
      p_organizacion_id: organizacionId,
    })

    if (error) throw error

    const result: Record<string, string> = {}
    for (const row of data ?? []) {
      if (!row.cliente_id || !row.ultimo_envio) continue
      result[row.cliente_id] = row.ultimo_envio
    }

    return result
  },

  async registrarEnvioManual(
    client: SupabaseClient<Database>,
    input: RegistrarEnvioManualInput,
  ): Promise<void> {
    const subject = `Estado de cuenta enviado manualmente — ${input.destinatarioNombre}`
    const { error } = await client
      .from('email_log')
      .insert({
        organizacion_id: input.organizacionId,
        cliente_id: input.clienteId,
        tipo: input.tipo,
        from: input.enviadoPorEmail,
        to: [input.destinatarioEmail || `manual:${input.destinatarioNombre}`],
        subject,
        status: 'manual',
        enviado_por_email: input.enviadoPorEmail,
        metadata: {
          canal: input.canal,
          notas: input.notas?.trim() || null,
        },
      })

    if (error) throw error
  },
}

export interface RegistrarEnvioManualInput {
  organizacionId: string
  clienteId: string
  tipo: 'estado_cuenta'
  destinatarioEmail: string
  destinatarioNombre: string
  canal: 'whatsapp' | 'telefono' | 'persona' | 'otro'
  notas?: string
  enviadoPorEmail: string
}
